import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Delivered from "../../../../assets/customSVG/inbox/Delivered";
import Loader from "../../../../components/commonComponents/Loader/Index";
import { ICONS } from "../../../../assets/icons";
import { inboxCustomerJourney } from "../../api/Api";
import { cn } from "../../../../helper/cn";
import { Typography } from "antd";
const { Title } = Typography;


const DEFAULT_LIMIT = 20;

const groupJourneyByMonth = (items = []) => {
  if (!items?.length) return [];
  const formatter = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
  const grouped = items.reduce((acc, item) => {
    const sendAt = item?.send_at ? new Date(item.send_at) : null;
    const monthYear = sendAt ? formatter.format(sendAt) : "Unknown";
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(item);
    return acc;
  }, {});
  return Object.entries(grouped).map(([label, groupedItems]) => ({
    label,
    items: groupedItems,
  }));
};

const CustomerJourneyTimeline = ({
  brandId,
  contactConversationId,
  contactPersonId,
  limit = DEFAULT_LIMIT,
  showHeader = true,
  className = "",
  contentClassName = "",
  contentHeight,
  fetchJourneyApi = inboxCustomerJourney,
  title = "Customer Journey",
}) => {
  const scrollContainerId = useMemo(
    () => `journey-scroll-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState("");

  const canQuery = brandId && (contactConversationId || contactPersonId);

  const sortedJourney = useMemo(() => {
    if (!items?.length) return [];
    return [...items].sort((a, b) => new Date(b?.send_at) - new Date(a?.send_at));
  }, [items]);

  const groupedJourney = useMemo(() => groupJourneyByMonth(sortedJourney), [sortedJourney]);

  const buildPayload = useCallback(
    (requestedPage) => {
      const payload = {
        brand_id: brandId,
        limit,
        page: requestedPage,
      };
      if (contactConversationId) payload.contact_conversation_id = contactConversationId;
      else if (contactPersonId) payload.contact_person_id = contactPersonId;
      return payload;
    },
    [brandId, contactConversationId, contactPersonId, limit]
  );

  const fetchJourney = useCallback(
    async (requestedPage = 1) => {
      if (!canQuery) {
        setItems([]);
        setHasMore(false);
        return;
      }
      setError("");
      if (requestedPage === 1) setInitialLoading(true);

      try {
        const payload = buildPayload(requestedPage);
        const response = await fetchJourneyApi(payload);
        const journey = response?.data?.customer_journey || [];
        const totalPages = response?.data?.total_pages || 1;
        const currentPage = response?.data?.page || requestedPage;

        setItems((prev) =>
          requestedPage === 1 ? journey : [...prev, ...journey]
        );

        setHasMore(currentPage < totalPages);
        setPage(currentPage + 1);
      } catch (fetchError) {
        console.error("Failed to fetch customer journey", fetchError);
        setError(
          fetchError?.response?.data?.error ||
          fetchError?.message ||
          "Unable to fetch journey"
        );
        setHasMore(false);
      } finally {
        setInitialLoading(false);
      }
    },
    [buildPayload, canQuery, fetchJourneyApi]
  );

  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    if (canQuery) {
      fetchJourney(1);
    }
  }, [canQuery, fetchJourney]);

  const renderTimeline = () => {
    if (error) {
      return (
        <div className="py-10 text-center text-red-500 text-sm">{error}</div>
      );
    }

    if (!initialLoading && !groupedJourney.length)
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
          <img
            src={ICONS?.emptyLoader}
            alt="empty state"
            className="w-[120px]"
          />
          {/* <div className="text-base font-semibold text-[var(--textBlack)]">
            No data found
          </div> */}
          <div className="text-sm text-[var(--font-600)]">
            We will display the journey once we have more activity.
          </div>
        </div>
      );

    return (
      <InfiniteScroll
        dataLength={items.length}
        next={() => fetchJourney(page)}
        hasMore={hasMore}
        loader={
          <div className="py-4 text-center text-sm text-[var(--font-600)] flex justify-center ">
            <Loader Width={20} Height={20} />
          </div>
        }
        endMessage={
          items.length > 0 && (
            <div className="py-4 text-center text-xs text-[var(--font-600)]">
              You have reached the end of the journey
            </div>
          )
        }
        scrollableTarget={scrollContainerId}
      >
        {groupedJourney.map(({ label, items: groupItems }) => (
          <div key={label} className="mb-10">
            <div className="ml-5 my-2">
              <span className="journey14px-500 inline-flex rounded bg-[var(--BG-25)] px-[15px] py-[6px] text-[0.75rem] font-semibold text-[var(--font-600)]">
                {label}
              </span>
            </div>
            {groupItems.map((item, idx) => {
              const dateObj = item?.send_at ? new Date(item.send_at) : null;
              const formattedDate = dateObj
                ? dateObj.toLocaleString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "Unknown time";

              // Define isNote here
              const isNote = item?.type === "note";
              const noteContent = isNote && item?.settings?.content;

              return (
                <div
                  key={`${item?.id}-${item?.send_at}`}
                  className={cn(
                    "relative ml-5 mr-5 flex items-start gap-4 pl-8 pt-5",
                    idx !== groupItems.length - 1 && "pb-2"
                  )}
                >
                  <div className="flex flex-col items-center pt-[6px]">
                    <div className="size-2 rounded-full bg-[#7B54FF]" />
                    {idx !== groupItems.length - 1 && (
                      <div className="mt-2 min-h-[42px] w-px flex-1 border border-1 border-[#e5e7eb]" />
                    )}
                  </div>
                  <div className="flex flex-1 flex-col space-y-1">
                    <span className="journey14px-500 flex items-start gap-2 text-[var(--textBlack)]">
                      {item?.status === "READ" && (
                        <span className="mt-[2px]">
                          <Delivered color="#1778F2" />
                        </span>
                      )}
                      {item?.message || "--"}
                    </span>
                    {/* {isNote && noteContent && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded p-2 my-2 ml-1 text-sm text-yellow-800">
                        <span className="font-semibold mr-1">Note:</span>
                        {noteContent}
                      </div>
                    )} */}

                    {isNote && noteContent && (
                      <div className="flex items-center border-l-4 border-yellow-400 bg-yellow-50 rounded-lg p-3 my-2 shadow-sm max-w-[400px]">
                        {/* Optional icon can be placed here */}
                        <div className="w-full">
                          <div className="text-xs text-yellow-700 font-semibold mb-1">Note attached</div>
                          <div className="text-[13px] text-[#1d3557] leading-relaxed font-medium overflow-wrap">
                            {noteContent}
                          </div>
                        </div>
                      </div>
                    )}
                    <span className="inbox12px-500 text-[var(--font-600)]">
                      {formattedDate}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </InfiniteScroll>
    );
  };

  return (
    <div className={cn("flex h-full flex-col bg-white", className)}>
      {showHeader && (
        <div className="text-lg font-semibold text-gray-800 pl-4 py-4 border-b">{title}</div>
      )}
      <div
        id={scrollContainerId}
        className={cn(
          "overflow-y-auto bg-white px-2 pb-6",
          contentClassName,
          {
            "": showHeader,
            "pt-6": !showHeader,
          }
        )}
        style={
          contentHeight
            ? { maxHeight: contentHeight, minHeight: contentHeight }
            : { height: 400, minHeight: 300 }
        }
      >
        {initialLoading && !items.length ? (
          <div className="py-10 text-center text-sm text-[var(--font-600)] h-full w-full flex justify-center items-center">
            <Loader Width={20} Height={20} />
          </div>
        ) : (
          renderTimeline()
        )}
      </div>
    </div>
  );
};

export default CustomerJourneyTimeline;
